import { FC, useCallback, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import { Message, Messages, Spinner } from "capitalrx-components-2.0/lib/general";
import { Stack } from "capitalrx-components-2.0/lib/layout";

import { findBestPriceDrugGetAPI, findBestPriceLaunchGetAPI, findBestPricePricesListAPI } from "@/api";
import { FindBestPrice } from "@/api/interfaces";
import { AuthenticationContext } from "@/context";
import { CheckCoverageCostBreakdown } from "./CheckCoverageCostBreakdown";
import { CheckCoverageForm, CheckCoverageFormData, initialFormValues } from "./CheckCoverageForm";
import { CheckCoverageResults, SortOption } from "./CheckCoverageResults";
import { CheckCoverageSearch, pluralizeQuantity, packagingLabel } from "./utils";

type Step = "form" | "results" | "cost_breakdown";

function errorMessage(err: any, fallback: string): string {
  return err?.response?.data?.message || fallback;
}

export const CheckCoverage: FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const { getAuthToken } = useContext(AuthenticationContext);

  const [step, setStep] = useState<Step>("form");
  const [drugResponse, setDrugResponse] = useState<FindBestPrice.DrugGetResponse>();
  const [formValues, setFormValues] = useState<CheckCoverageFormData>();
  const [loadError, setLoadError] = useState<string>();

  const [search, setSearch] = useState<CheckCoverageSearch>();
  const [pageNumber, setPageNumber] = useState(1);
  const [sortBy, setSortBy] = useState<SortOption>("distance");
  const [pricesResponse, setPricesResponse] = useState<FindBestPrice.PricesListResponse>();
  const [isLoadingPrices, setIsLoadingPrices] = useState(false);
  const [pricesError, setPricesError] = useState<string>();
  const [selectedPrice, setSelectedPrice] = useState<FindBestPrice.Price>();

  useEffect(() => {
    if (!token) {
      setLoadError("This coverage check link is invalid. Please return to Patient Lookup and try again.");
      return;
    }

    const loadDrug = async () => {
      try {
        const authToken = getAuthToken();
        const [launch, drug] = await Promise.all([
          findBestPriceLaunchGetAPI({ token }, authToken),
          findBestPriceDrugGetAPI({ token }, authToken),
        ]);
        setDrugResponse(drug);
        setFormValues(initialFormValues(drug.drug, launch.ndc));
      } catch (err) {
        console.error("Failed to load check coverage drug:", err);
        setLoadError(
          "This coverage check has expired or is unavailable. Please return to Patient Lookup / Drug Coverage and try again.",
        );
      }
    };

    loadDrug();
  }, [token]);

  const fetchPrices = useCallback(async (nextSearch: CheckCoverageSearch, nextPageNumber: number) => {
    setIsLoadingPrices(true);
    setPricesError(undefined);
    setPageNumber(nextPageNumber);

    try {
      const response = await findBestPricePricesListAPI(
        {
          token,
          drug_id: nextSearch.drug.id,
          dosage_form: nextSearch.dosage_form,
          dosage: nextSearch.dosage,
          quantity: nextSearch.quantity,
          days_supply: nextSearch.days_supply,
          location: nextSearch.location,
          page_number: nextPageNumber,
        },
        getAuthToken(),
      );
      setPricesResponse(response);
    } catch (err) {
      console.error("Failed to load check coverage prices:", err);
      setPricesResponse(undefined);
      setPricesError(errorMessage(err, "Could not generate results for the chosen drug."));
    } finally {
      setIsLoadingPrices(false);
    }
  }, [token]);

  const handleSearch = useCallback((nextSearch: CheckCoverageSearch, values: CheckCoverageFormData) => {
    setFormValues(values);
    setSearch(nextSearch);
    setStep("results");
    fetchPrices(nextSearch, 1);
  }, [fetchPrices]);

  const handleSelectPrice = useCallback((price: FindBestPrice.Price) => {
    setSelectedPrice(price);
    setStep("cost_breakdown");
    window.scrollTo(0, 0);
  }, []);

  if (loadError) {
    return (
      <Message appearance={Messages.Error} visible>
        {loadError}
      </Message>
    );
  }

  if (!drugResponse || !formValues) {
    return (
      <Stack alignment="center">
        <Spinner size="large" />
      </Stack>
    );
  }

  const quantityLabel = search
    ? pluralizeQuantity(search.quantity, packagingLabel(search.drug, search.dosage_form))
    : "";

  if (step === "cost_breakdown" && search && selectedPrice) {
    return (
      <CheckCoverageCostBreakdown
        token={token}
        search={search}
        quantityLabel={quantityLabel}
        price={selectedPrice}
        onBack={() => setStep("results")}
      />
    );
  }

  if (step === "results" && search) {
    return (
      <CheckCoverageResults
        search={search}
        quantityLabel={quantityLabel}
        pricesResponse={pricesResponse}
        isLoading={isLoadingPrices}
        error={pricesError}
        pageNumber={pageNumber}
        sortBy={sortBy}
        onSortChange={setSortBy}
        onPageChange={(nextPageNumber) => fetchPrices(search, nextPageNumber)}
        onSelect={handleSelectPrice}
        onBack={() => setStep("form")}
      />
    );
  }

  return (
    <CheckCoverageForm
      token={token}
      drugResponse={drugResponse}
      defaultValues={formValues}
      onSearch={handleSearch}
    />
  );
};
